import { createSelector, createSlice } from "@reduxjs/toolkit";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory } from "../../common/reducers/utilities";
import { logout } from "../../common/reducers/initContext";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = i => i.id;

const inventoryMgrSlice = createSlice({
    name: 'mgrInventory',
    initialState: {
        data: {}
    },
    reducers: {
        setForCompartment: (state, action) => {
            ensureArray(action.payload.compartmentids).forEach(compartmentid => delete state.data[compartmentid]);
            const toSet = ensureArray(action.payload.data);
            const byCompartment = groupBy(toSet, 'compartmentid');
            Object.entries(byCompartment).forEach(([ compartmentid, inventory ]) => {
                state.data[compartmentid] = inventory;
            });
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byCompartment = groupBy(toUpdate, 'compartmentid');
            Object.entries(byCompartment).forEach(([ compartmentid, inventory ]) => {
                const ids = new Set(inventory.map(i => key(i)));
                state.data[compartmentid] = (state.data[compartmentid]?.filter(i => !ids.has(key(i))) ?? []).concat(inventory);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byCompartment = groupBy(toRemove, 'compartmentid');
            Object.entries(byCompartment).forEach(([ compartmentid, inventory ]) => {
                if (state.data[compartmentid]) {
                    const ids = new Set(inventory.map(i => key(i)));
                    state.data[compartmentid] = state.data[compartmentid].filter(i => !ids.has(key(i)));
                    if (state.data[compartmentid].length === 0) {
                        delete state.data[compartmentid];
                    }
                }
            });
        },
        removeForCompartment: (state, action) => {
            ensureArray(action.payload).forEach(c => delete state.data[c.id]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => inventoryMgrSlice.getInitialState());
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'inventory_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'inventory_one');

const fetchInventoryForCompartment = aggregatingFetch(
    id => network => network.post('/api/v2/mgrInventoryByCompartment', [id], data => inventoryMgrSlice.actions.setForCompartment({ compartmentids: [id], data}), visualizeFailure('get')),
    ids => network => network.post('/api/v2/mgrInventoryByCompartment', ids, data => inventoryMgrSlice.actions.setForCompartment({ compartmentids: ids, data}), visualizeFailure('get')),
    )

// #endregion

// #region selectors
const selectAllInventory = state => state.mgrInventory.data;
const selectInventoryByCompartment = compartmentid => createSelector([selectAllInventory], inventory => inventory[compartmentid] ?? []);
// #endregion

const listen = {
    inventoryCreated: (inventory, { dispatch, websocket }) => {
        markFetched(websocket, 'inventory', key(inventory))
        dispatch(inventoryMgrSlice.actions.addOrUpdate(inventory))
    },
    inventoryUpdated: (inventory, { dispatch }) => dispatch(inventoryMgrSlice.actions.addOrUpdate(inventory)),
    inventoryRemoved: (inventory, { dispatch }) => dispatch(inventoryMgrSlice.actions.remove(inventory)),
    compartmentRemoved: (compartment, { dispatch }) => dispatch(inventoryMgrSlice.actions.removeForCompartment(compartment))
}

const inventoryInitByID = initMapFactory(fetchInventoryForCompartment, listen);
registerEntityBasedFetch('compartment', inventoryInitByID);

const cleanup = { root: inventoryMgrSlice.name,  all: state => Object.values(selectAllInventory(state)).flat(), removeAll: inventoryMgrSlice.actions.remove, key };

export const [useMgrInventory] = dataHookFactory(selectInventoryByCompartment, initMultiMapFactory(inventoryInitByID, listen), {cleanup});

const reducer = { name: inventoryMgrSlice.name, reducer: inventoryMgrSlice.reducer }
export default reducer;